/**
 * The add/edit-record FORM STATE — everything `WalletEntry` (phone keypad
 * screen) and `DesktopEntry` (MDM-style desktop form) share: the draft, the
 * remembered context, F4 recent entries, F5 clone, F7 rules (remark +
 * sub-category on a new entry), templates, "same as last", duplicate, custom
 * fields and the save itself. Extracted verbatim from `WalletEntry` so the two
 * layouts can never drift apart on a rule; only the rendering differs.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import type { TemplatePrefill } from "@/components/templates";

import { useExtraFields } from "./ExtraFields";
import { calcPaise } from "./money";
import {
  activeCategoryId,
  applyContext,
  applyDraftPrefill,
  canSave,
  categoryKindFor,
  contextFromDraft,
  draftForNextEntry,
  draftFromRecord,
  draftFromRecordForClone,
  draftProblems,
  emptyDraft,
  switchType,
  today,
  type EntryDraft,
} from "./draft";
import {
  readLastAccountId,
  readLastContext,
  readLastEntry,
  reconcileAccountId,
  rememberAccountId,
  rememberContext,
  rememberLastEntry,
  useAccounts,
  useCategories,
  useLabels,
  useTopCategories,
} from "./useEntryData";
import { useSaveEntry } from "./useSaveEntry";
import type { EntryRecord, EntryType, SavedEntry } from "./types";
import { useRecentEntries } from "./RecentEntries";

/** What the post-save strip needs to offer a duplicate. */
export interface JustSaved {
  id: string;
  type: EntryType;
  amountPaise: number;
  /** The draft as saved — a duplicate is this, with no record id attached. */
  draft: EntryDraft;
}

export interface EntryFormOptions {
  record?: EntryRecord;
  cloneFrom?: EntryRecord;
  defaultType?: EntryType;
  onSaved?: (saved: SavedEntry) => void;
}

export function useEntryForm({
  record,
  cloneFrom,
  defaultType = "expense",
  onSaved,
}: EntryFormOptions) {
  const [draft, setDraft] = useState<EntryDraft>(() =>
    record
      ? draftFromRecord(record)
      : cloneFrom
        ? draftFromRecordForClone(cloneFrom)
        : emptyDraft(defaultType),
  );
  /**
   * Present = this save is a `PUT` against an existing row. Duplicating clears
   * it, which is the entire mechanism that turns a copy into a new record.
   */
  const [recordId, setRecordId] = useState<string | undefined>(record?.id);
  const [justSaved, setJustSaved] = useState<JustSaved | null>(null);
  /**
   * Nothing is marked wrong until a save has been tried. A form that opens
   * already shouting in red at a user who has not typed anything yet is
   * telling them off for arriving.
   */
  const [attempted, setAttempted] = useState(false);

  const isEdit = Boolean(recordId);
  const kind = categoryKindFor(draft.type);

  const { data: categories = [] } = useCategories(kind);
  const { data: accounts = [], isSuccess: accountsLoaded } = useAccounts();
  const { data: labels = [] } = useLabels();
  const { data: topCategories = [] } = useTopCategories(
    draft.type === "transfer" ? null : draft.type,
  );
  const custom = useExtraFields(draft.type, recordId);
  const save = useSaveEntry();
  const { data: recentEntries = [] } = useRecentEntries(draft.type, !isEdit && !cloneFrom);

  /**
   * Preselect the last account used, once the live list can confirm it still
   * exists. A remembered id belonging to an archived account (or to another
   * user on a shared browser) would render as "no account" while still being
   * POSTed, so it is reconciled rather than trusted.
   *
   * LAYERED ON TOP: the per-type remembered context (category, account,
   * payment mode — `EntryContext` in `draft.ts`) from the LAST entry of THIS
   * type. It fills only what is still empty after the block above, so it
   * never fights the single-spendable-account default and never fires on an
   * edit. This is what turns the multi-account case — previously always a
   * tap — into a pre-filled field, same as the single-account case already
   * was. Category and payment mode are new fills the old effect never made.
   */
  useEffect(() => {
    if (!accountsLoaded || isEdit) return;
    setDraft((current) => {
      let next = current;

      if (!next.accountId && !next.fromAccountId) {
        let remembered = reconcileAccountId(readLastAccountId(), accounts);
        // FIRST USE has nothing remembered, so the field opened empty and
        // every brand-new user paid a tap on their very first entry — the one
        // where the app most needs to feel quick. When there is exactly ONE
        // spendable account there is no choice to make, so making it is not
        // a guess.
        //
        // Deliberately restricted to a single candidate: with two or more, a
        // default would be a guess about which one, and a wrong account is a
        // silent error (the entry saves, against the wrong balance) rather
        // than a visible one.
        if (!remembered) {
          // Liquid-only, matching the split the rest of the app uses: an FD
          // or RD is not somewhere a grocery bill is paid from, so a lone
          // deposit must NOT become the default.
          const LOCKED = new Set(["FD", "RD"]);
          const spendable = accounts.filter(
            (a) => !a.isArchived && !LOCKED.has((a.accountType ?? "").toUpperCase()),
          );
          if (spendable.length === 1) remembered = spendable[0].id;
        }
        if (remembered) {
          next =
            next.type === "transfer"
              ? { ...next, fromAccountId: remembered }
              : { ...next, accountId: remembered };
        }
      }

      const ctx = readLastContext(next.type);
      if (ctx) {
        const categoryId =
          ctx.categoryId && categories.some((c) => c.id === ctx.categoryId)
            ? ctx.categoryId
            : null;
        const accountId = reconcileAccountId(ctx.accountId, accounts);
        next = applyContext(next, { categoryId, accountId, paymentMethod: ctx.paymentMethod });
      }

      return next;
    });
  }, [accountsLoaded, accounts, categories, isEdit, draft.type]);

  /**
   * The "remembered" markers next to the category and account fields — pure
   * comparisons against the stored context, so they read correctly whether
   * the field was auto-filled by the effect above or the user simply picked
   * the same thing again. No separate "was this auto-filled" flag to keep in
   * sync, and it clears itself the instant the current value diverges.
   */
  const lastContext = useMemo(() => readLastContext(draft.type), [draft.type]);
  const categoryIsRemembered =
    !isEdit &&
    kind !== null &&
    Boolean(lastContext?.categoryId) &&
    activeCategoryId(draft) === lastContext?.categoryId;
  const accountIsRemembered =
    !isEdit &&
    Boolean(lastContext?.accountId) &&
    (draft.type === "transfer" ? draft.fromAccountId : draft.accountId) === lastContext?.accountId;

  /** Amount preview for the "Same as last" chip — null hides it entirely
   *  (nothing saved yet this browser, or its amount somehow didn't parse). */
  const repeatCandidatePaise = useMemo(() => {
    if (isEdit) return null;
    const last = readLastEntry();
    return last ? calcPaise(last.calc) : null;
  }, [isEdit]);

  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === activeCategoryId(draft)) ?? null,
    [categories, draft],
  );
  const accountById = (id: string) => accounts.find((a) => a.id === id) ?? null;

  // F7: a NEW entry needs a remark and, under a parent, a sub-category.
  const rules = isEdit ? undefined : { categories };
  const allProblems = draftProblems(draft, rules);
  const problems = attempted ? allProblems : [];
  const ready = canSave(draft, rules) && custom.isValid && !save.isPending;

  function update(next: Partial<EntryDraft>) {
    setDraft((current) => ({ ...current, ...next }));
    setJustSaved(null);
  }

  function onTypeChange(next: EntryType) {
    setDraft((current) => switchType(current, next));
    setJustSaved(null);
  }

  /**
   * `overrideDraft` lets a caller that has just BUILT a draft (the template
   * "fill and save" one-tap button) submit it immediately, without waiting a
   * render for `setDraft` to flush and without duplicating everything below.
   * Every other call site omits it and this reads from state as before.
   */
  async function submit(then: "close" | "again", overrideDraft?: EntryDraft) {
    setAttempted(true);
    const activeDraft = overrideDraft ?? draft;
    if (overrideDraft) setDraft(overrideDraft);
    if (!canSave(activeDraft, rules) || !custom.isValid || save.isPending) return;
    try {
      const saved = await save.mutateAsync({ draft: activeDraft, recordId });

      // Custom-field values can only be written once the record has an id, so
      // this runs after the record's own request. A failure here means the
      // RECORD saved and its extra fields did not — said precisely rather than
      // implying the whole entry failed.
      try {
        await custom.save(saved.id);
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Saved, but the custom fields did not",
        );
      }

      const usedAccount =
        activeDraft.type === "transfer" ? activeDraft.fromAccountId : activeDraft.accountId;
      rememberAccountId(usedAccount);
      // Remember-last-context (per type) and the full "same as last" clone —
      // both write on EVERY save, create or edit, so the very next entry (of
      // any kind) benefits immediately.
      rememberContext(activeDraft.type, contextFromDraft(activeDraft));
      rememberLastEntry(activeDraft);

      toast.success(isEdit ? "Record updated" : "Record saved");
      setJustSaved({
        id: saved.id,
        type: activeDraft.type,
        amountPaise: saved.amountPaise,
        draft: activeDraft,
      });
      onSaved?.({ ...saved, keepOpen: then === "again" });

      setAttempted(false);
      if (then === "again") {
        // Keep the context (type, date, accounts, category, labels), clear the
        // record (amount, note). And detach: the next save is a CREATE.
        setDraft(draftForNextEntry(activeDraft));
        setRecordId(undefined);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not save");
    }
  }

  /** Fill the form from a picked template. Never saves — the user confirms. */
  function applyTemplatePrefill(p: TemplatePrefill) {
    setDraft((current) => applyDraftPrefill(current, p));
    setJustSaved(null);
  }

  /** The template picker's one-tap "fill and save" — never offered on an
   *  edit (`WalletEntry.tsx` gates it below), so this is always a CREATE. */
  function applyTemplatePrefillAndSave(p: TemplatePrefill) {
    const next = applyDraftPrefill(draft, p);
    void submit("close", next);
  }

  /**
   * "Same as last" — clones the most recently saved entry (any type) into a
   * fresh draft. Safe against `NEVER_CLONED` (`draft.ts`) the same way
   * `duplicate` below is: `EntryDraft` structurally carries no `id` and no
   * `externalSourceId`, so there is nothing to strip. Only offered on a
   * FRESH, untouched entry — it would be a strange surprise to overwrite
   * something the user already started typing.
   */
  function repeatLastEntry() {
    const last = readLastEntry();
    if (!last) return;
    setDraft({ ...last, date: today() });
    setRecordId(undefined);
    setJustSaved(null);
  }

  /**
   * DUPLICATE — a new record, pre-filled, never a resave of the old one.
   *
   * Two things make it a new record and both are structural rather than
   * remembered-at-the-call-site:
   *
   *  1. `setRecordId(undefined)` — the save path builds a `POST`, not a
   *     `PUT /{id}`.
   *  2. The draft has no `externalSourceId` FIELD AT ALL, and `entryBody` has
   *     no branch that can emit that key. So the clone cannot inherit the
   *     UNIQUE-per-user sync tag from a rent-synced or imported row. That
   *     matters because the server's create schema does not accept the column:
   *     a clone that carried it would be silently ignored on a good day and
   *     collide on a bad one — dropped by the server with nothing for the UI
   *     to show.
   */
  function duplicate(from: JustSaved) {
    setDraft(from.draft);
    setRecordId(undefined);
    setJustSaved(null);
    toast.info("Duplicated — check it over and save to create a second record");
  }

  /** Duplicating while EDITING: same rule, applied to the row on screen. */
  function duplicateCurrent() {
    setRecordId(undefined);
    setJustSaved(null);
    toast.info("Editing a copy — saving will create a new record");
  }

  return {
    draft,
    setDraft,
    recordId,
    justSaved,
    setJustSaved,
    attempted,
    setAttempted,
    isEdit,
    kind,
    categories,
    accounts,
    labels,
    topCategories,
    custom,
    save,
    recentEntries,
    categoryIsRemembered,
    accountIsRemembered,
    repeatCandidatePaise,
    selectedCategory,
    accountById,
    rules,
    allProblems,
    problems,
    ready,
    update,
    onTypeChange,
    submit,
    applyTemplatePrefill,
    applyTemplatePrefillAndSave,
    repeatLastEntry,
    duplicate,
    duplicateCurrent,
  };
}

export type EntryForm = ReturnType<typeof useEntryForm>;
